import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import { OfferConfig, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';

type SubmitPreferredTermRequestBody =
  paths['/offers/preferred-term']['post']['requestBody']['content']['application/json'];

type PreferredTermPostResponse =
  paths['/offers/preferred-term']['post']['responses']['201']['content']['application/json'];

type PreferredTerm202Response =
  paths['/offers/preferred-term']['post']['responses']['202']['content']['application/json'];

async function submitPreferredTermOfferSEA(body: SubmitPreferredTermRequestBody) {
  const response = await apiClient.post('api/supplier-experience/offers/preferred-term', { json: body });

  if (response.status === 201) {
    const responseBody = await response.json<PreferredTermPostResponse>();
    return { status: response.status, offers: responseBody.data };
  }

  if (response.status === 202) {
    const responseBody = await response.json<PreferredTerm202Response>();
    // Normalize 202 offers to match the supplierMarkets structure expected by onSuccess
    const offers = responseBody.data.offers.map((o) => ({
      ...o,
      supplierMarkets: [{ marketUuid: o.marketUuid, supplierDivisionUuid: o.supplierDivisionUuid }],
    }));
    return { status: response.status, offers };
  }

  return { status: response.status, offers: undefined };
}

export interface SubmitPreferredTermOfferInput {
  takerMarkets: TakerMarket[];
  rate: number;
  rateType: 'APR' | 'DISC';
  expirationDate: string;
  renewalInformation?: { enabled: boolean; termDays?: number };
}

/**
 * Hook for submitting preferred term offers
 * Returns a mutation that handles errors internally via DataDog
 * and triggers the onError handler without requiring a catch in the component
 */
const useSubmitPreferredTermOffer = () => {
  const queryClient = useQueryClient();
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  const mutation = useMutation({
    mutationKey: ['submitPreferredTermOffer'],
    mutationFn: async ({
      takerMarkets,
      rate,
      rateType,
      expirationDate,
      renewalInformation,
    }: SubmitPreferredTermOfferInput) => {
      const body: SubmitPreferredTermRequestBody = {
        offerType: 'preferred-term' as const,
        supplierMarkets: takerMarkets.map((tm) => ({
          makerOrganizationUuid: tm.makerOrganizationUuid,
          marketUuid: tm.marketUuid,
          supplierDivisionUuid: tm.takerDivisionUuid,
        })),
        rate,
        rateType,
        expirationDate,
        ...(renewalInformation && { renewalInformation }),
        referrer: { workflow: 'OFFER_BUILDER' as const },
      };

      return submitPreferredTermOfferSEA(body);
    },
    onSuccess: (result, { takerMarkets, rate, rateType }) => {
      takerMarkets.forEach((effectedTm) => {
        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) => {
          return (prevTakerMarkets ?? []).map((prevTm) => {
            const isMatch =
              prevTm.marketUuid === effectedTm.marketUuid && prevTm.takerDivisionUuid === effectedTm.takerDivisionUuid;

            if (!isMatch) {
              return prevTm;
            }

            // If placing first offer, use returned offer uuid from API response to update cache
            // User can interact with offer before stats and offer refetches
            const offerUuid =
              prevTm.offerConfig.uuid ??
              result.offers?.find((offer) =>
                offer.supplierMarkets.some(
                  (sm) =>
                    sm.marketUuid === effectedTm.marketUuid && sm.supplierDivisionUuid === effectedTm.takerDivisionUuid
                )
              )?.uuid;

            const maxApr = rateType === 'APR' ? rate : null;
            const maxDiscount = rateType === 'DISC' ? rate : null;
            const isDiscountBidding = rateType === 'DISC';

            const offerConfig: OfferConfig = {
              ...prevTm.offerConfig,
              uuid: offerUuid,
              maxApr,
              maxDiscount,
              isDiscountBidding,
              isEnabled: true,
              offerType: 'PREFERRED_TERM',
            };

            return {
              ...prevTm,
              offerConfig,
            };
          });
        });
      });

      if (result.status === 202) {
        addPendingMarkets(
          takerMarkets.map((effectedTm) => ({
            marketUuid: effectedTm.marketUuid,
            takerDivisionUuid: effectedTm.takerDivisionUuid,
            makerOrganizationUuid: effectedTm.makerOrganizationUuid,
          }))
        );
      } else {
        // offer resolved synchronously, refetch immediately
        takerMarkets.forEach((effectedTm) => {
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: effectedTm.marketUuid,
            takerDivisionUuid: effectedTm.takerDivisionUuid,
            makerOrganizationUuid: effectedTm.makerOrganizationUuid,
          });
        });
        refetchManyTakerMarkets(
          takerMarkets.map((effectedTm) => ({
            marketUuid: effectedTm.marketUuid,
            takerUuid: effectedTm.takerDivisionUuid,
          }))
        );
      }
    },
    onError: (error, variables) => {
      addError('useSubmitPreferredTermOffer', {
        error,
        variables,
      });
    },
  });

  return mutation;
};

export default useSubmitPreferredTermOffer;
